import { StandardRequest, StandardResponse } from "./functions";
import { ITagAssociationMeta } from "./tag";
import { IEntityMemoryMeta, IEntityMediaMeta, IEntityContactMeta, IEntityGroupMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "./entity-links";
import type { MemoryModel } from "./memories";
import type { MediaModel } from "./media";
import type { ContactModel } from "./contacts";
import type { PlaceModel } from "./places";
import type { AnchorModel } from "./anchors";
import { IUnifiedFilter } from "./filters";
/**
 * @swagger
 *  components:
 *    schemas:
 *      Group:
 *        type: object
 *        required: [ "name", "description", "ownerId", "contactIds", "createdAt" ]
 *        properties:
 *          id:
 *            type: string
 *          name:
 *            type: string
 *          description:
 *            type: string
 *          ownerId:
 *            type: string
 *          contactIds:
 *            type: array
 *            items:
 *              type: string
 *          childGroupIds:
 *            type: array
 *            items:
 *              type: string
 *          groupType:
 *            type: string
 *            enum: [ "standard", "allcontacts" ]
 *          createdAt:
 *            type: number
 *      GroupModel:
 *        allOf:
 *          - $ref: '#/components/schemas/Group'
 *          - type: object
 *            properties:
 *              ownerDisplayName:
 *                type: string
 *              memberDisplayNames:
 *                type: array
 *                items:
 *                  type: string
 */

/**
 * Raw persisted database document schema for a Group entity in Firestore (`groups/{id}`).
 */
export interface IGroup {
  id?: string;
  name: string;
  description: string;
  ownerId: string;
  contacts: Record<string, IEntityContactMeta>;
  childGroups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of contact IDs maintained for Firestore array-contains query indexing */
  contactIds?: string[];
  /** Companion synchronized array of child group IDs maintained for Firestore array-contains query indexing */
  childGroupIds?: string[];
  groupType?: "standard" | "allcontacts" | "system";
  /** Foreign key pointing to media.id for group picture */
  photoMediaId?: string;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Companion synchronized array of media IDs maintained for Firestore query indexing */
  mediaIds?: string[];
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
  /** 1-5 star favourite rating */
  favouriteRating?: number;
  createdAt: number;
  updatedAt?: number;
}

export type Group = IGroup;

import { ShareSummary, SharedToSummary } from "./shares";


export type GroupModel = Group & {
  ownerDisplayName?: string;
  memberDisplayNames?: string[];
  contactItems?: ContactModel[];
  groupItems?: GroupModel[];
  memoryItems?: MemoryModel[];
  mediaItems?: MediaModel[];
  placeItems?: PlaceModel[];
  anchorItems?: AnchorModel[];
  userRole?: "owner" | "editor" | "viewer";
  canShare?: boolean;
  sharedWith?: ShareSummary[];
  sharedTo?: SharedToSummary[];
  /**
   * Group picture URL resolved from linked media or custom upload.
   */
  photoUrl?: string;
  /**
   * 50x50 group icon URL resolved from linked media or custom upload.
   */
  photoIconUrl?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      CreateGroupRequest:
 *        type: object
 *        required: [ "name", "description" ]
 *        properties:
 *          name:
 *            type: string
 *          description:
 *            type: string
 *          contacts:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/GroupContactMeta'
 *          childGroups:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/GroupChildGroupMeta'
 */
export type CreateGroupRequest = StandardRequest & {
  name: string;
  description: string;
  contacts?: Record<string, IEntityContactMeta>;
  childGroups?: Record<string, IEntityGroupMeta>;
  groupType?: "standard" | "allcontacts";
  /** Foreign key pointing to media.id for group picture */
  photoMediaId?: string;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
  /** 1-5 star favourite rating */
  favouriteRating?: number;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      CreateGroupResponse:
 *        type: object
 *        required: [ "groupId" ]
 *        properties:
 *          groupId:
 *            type: string
 *          group:
 *            $ref: '#/components/schemas/Group'
 */
export type CreateGroupResponse = StandardResponse & {
  groupId: string;
  group?: GroupModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      AddContactToGroupRequest:
 *        type: object
 *        required: [ "groupId", "contactId" ]
 *        properties:
 *          groupId:
 *            type: string
 *          contactId:
 *            type: string
 */
export type AddContactToGroupRequest = StandardRequest & {
  groupId: string;
  contactId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      RemoveContactFromGroupRequest:
 *        type: object
 *        required: [ "groupId", "contactId" ]
 *        properties:
 *          groupId:
 *            type: string
 *          contactId:
 *            type: string
 */
export type RemoveContactFromGroupRequest = StandardRequest & {
  groupId: string;
  contactId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      EditGroupRequest:
 *        type: object
 *        required: [ "groupId", "name", "description" ]
 *        properties:
 *          groupId:
 *            type: string
 *          name:
 *            type: string
 *          contacts:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/GroupContactMeta'
 *          childGroups:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/GroupChildGroupMeta'
 */
export type EditGroupRequest = StandardRequest & {
  groupId: string;
  name: string;
  description: string;
  contacts?: Record<string, IEntityContactMeta>;
  childGroups?: Record<string, IEntityGroupMeta>;
  /** Foreign key pointing to media.id for group picture */
  photoMediaId?: string;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
  /** 1-5 star favourite rating */
  favouriteRating?: number;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      DeleteGroupRequest:
 *        type: object
 *        required: [ "groupId" ]
 *        properties:
 *          groupId:
 *            type: string
 */
export type DeleteGroupRequest = StandardRequest & {
  groupId: string;
  cascadeUnlink?: boolean;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyGroupsRequest:
 *        type: object
 *        properties: {}
 */
export type GetMyGroupsRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyGroupsResponse:
 *        type: object
 *        required: [ "groups" ]
 *        properties:
 *          groups:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/GroupModel'
 */
export type GetMyGroupsResponse = StandardResponse & {
  groups: GroupModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllGroupsRequest:
 *        type: object
 *        properties: {}
 */
export type GetAllGroupsRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllGroupsResponse:
 *        type: object
 *        required: [ "groups" ]
 *        properties:
 *          groups:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/GroupModel'
 */
export type GetAllGroupsResponse = StandardResponse & {
  groups: GroupModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GroupFilter:
 *        type: object
 *        properties:
 *          name:
 *            type: string
 *            description: Regex pattern to filter groups by name
 */
export type GroupFilter = {
  name?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetGroupsRequest:
 *        type: object
 *        properties:
 *          filter:
 *            $ref: '#/components/schemas/GroupFilter'
 */
export type GetGroupsRequest = StandardRequest & {
  filter?: GroupFilter | IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetGroupsResponse:
 *        type: object
 *        required: [ "groups" ]
 *        properties:
 *          groups:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/GroupModel'
 */
export type GetGroupsResponse = StandardResponse & {
  groups: GroupModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyGroupRequest:
 *        type: object
 *        required: [ "groupId" ]
 *        properties:
 *          groupId:
 *            type: string
 */
export type GetMyGroupRequest = StandardRequest & {
  groupId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetGroupRequest:
 *        type: object
 *        required: [ "groupId" ]
 *        properties:
 *          groupId:
 *            type: string
 */
export type GetGroupRequest = GetMyGroupRequest;

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyGroupResponse:
 *        type: object
 *        properties:
 *          group:
 *            $ref: '#/components/schemas/Group'
 */
export type GetMyGroupResponse = StandardResponse & {
  group?: GroupModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetGroupResponse:
 *        type: object
 *        properties:
 *          group:
 *            $ref: '#/components/schemas/Group'
 */
export type GetGroupResponse = GetMyGroupResponse;
